import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TypewriterText } from '../hooks/useTypewriter';
import { useCopy, sections, find, Prose } from '../components/Copy';

// the words are backend/content/downloads.md and app.md; the app, its version and
// checksum, and the runtimes come from the registry (/api/downloads)

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kb`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(0)} mb`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} gb`;
}

function AppCard({ app, rooms, runtimes }) {
  const bundled = new Set((app.bundled_runtimes || []).map((r) => r.split('@')[0]));
  return (
    <article className="border-y border-[#1e251f] py-8 space-y-6" data-testid="app-mac">
      <header className="flex items-start gap-5">
        <img src="/apple-touch-icon.png" alt="" className="w-16 h-16 lg:w-20 lg:h-20 shrink-0 -ml-1" />
        <div className="space-y-2 min-w-0">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h3 className="text-2xl lg:text-3xl text-white lowercase">soma</h3>
            <span className="text-white/60 font-mono text-sm">{app.version}</span>
          </div>
          <p className="text-[#666666] font-mono text-xs lowercase">
            mac · apple silicon · {formatSize(app.size_bytes)}{app.released ? ` · released ${app.released}` : ''}
          </p>
        </div>
      </header>

      <Prose className="text-sm">{rooms}</Prose>

      {bundled.size > 0 && (
        <div className="border-t border-[#1e251f] pt-5">
          <p className="text-[#555555] font-mono text-[11px] lowercase mb-2">runtimes inside</p>
          <div className="flex flex-wrap gap-2">
            {runtimes.filter((r) => bundled.has(r.runtime_id)).map((r) => (
              <Link key={r.id} to={`/runtimes/${r.id}`}
                className="border border-[#383f38] rounded-full px-3 py-1 font-mono text-xs text-white/80 hover:text-white hover:border-white lowercase">
                {r.name} {r.runtime_version}{r.status === 'latest' ? ' · current' : ''}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4 pt-1">
        <a href={app.url} download
          className="border border-white bg-white text-black px-7 py-3 text-sm lowercase hover:bg-transparent hover:text-white transition-all duration-300 rounded-full"
          data-testid="mac-download-btn">
          download {app.name}
        </a>
        <span className="font-mono text-[10px] text-[#444444] break-all" title="sha256">sha256 {app.sha256}</span>
      </div>
    </article>
  );
}

function RuntimeRow({ runtime }) {
  const current = runtime.status === 'latest';
  return (
    <Link to={`/runtimes/${runtime.id}`}
      className="group block py-4"
      data-testid={`runtime-${runtime.id}`}>
      <div className="flex items-baseline gap-3 flex-wrap">
        <span className="text-white lowercase">{runtime.name}</span>
        <span className="text-white/50 font-mono text-xs">{runtime.runtime_version}</span>
        <span className={`font-mono text-[10px] lowercase px-2 py-0.5 rounded border ${current ? 'border-white/40 text-white bg-white/5' : 'border-[#383f38] text-[#888888]'}`}>
          {current ? 'current' : runtime.status}
        </span>
        <span className="ml-auto text-xs text-[#555555] lowercase">{runtime.released ? `released ${runtime.released}` : ''}</span>
      </div>
      <p className="text-[#888888] lowercase text-sm mt-1.5">{runtime.headline}</p>
      <span className="inline-block mt-2 text-xs text-white/50 group-hover:text-white lowercase transition-colors">spec, releases and bundle →</span>
    </Link>
  );
}

export default function Downloads() {
  const copy = sections(useCopy('downloads'));
  const rooms = useCopy('app');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('/api/downloads')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('failed to load downloads'))))
      .then(setData)
      .catch((e) => setError(e.message));
  }, []);

  const title = copy.find((s) => s.level === 1);
  const install = find(copy, 'install');
  const runtimesCopy = find(copy, 'runtimes');
  const app = data?.apps?.[0];

  return (
    <div className="min-h-screen bg-black py-12 lg:py-20 px-6 lg:px-8 page-wrapper" data-testid="downloads-page">
      <div className="max-w-3xl mx-auto space-y-12">
        <section>
          <h1 className="text-4xl lg:text-5xl font-bold text-white lowercase mb-4">
            <TypewriterText text={title?.title || 'downloads'} speed={65} startDelay={150} />
          </h1>
          <Prose>{title?.body}</Prose>
        </section>

        {error && <div className="text-[#ff8888] lowercase text-sm" data-testid="downloads-error">{error}</div>}
        {!data && !error && <div className="text-[#666666] lowercase text-sm" data-testid="downloads-loading">loading…</div>}

        {app && <AppCard app={app} rooms={rooms} runtimes={data.runtimes || []} />}

        {install && (
          <section data-testid="install-section">
            <h2 className="text-xs lowercase text-[#666666] tracking-wider mb-4">{install.title}</h2>
            <Prose className="text-sm">{install.body}</Prose>
          </section>
        )}

        {data?.runtimes?.length > 0 && (
          <section className="space-y-4" data-testid="runtimes-section">
            <h2 className="text-xs lowercase text-[#666666] tracking-wider">{runtimesCopy?.title || 'runtimes'}</h2>
            <Prose className="text-sm">{runtimesCopy?.body}</Prose>
            <div className="divide-y divide-[#1a201b] border-y border-[#1a201b]">
              {data.runtimes.map((r) => <RuntimeRow key={r.id} runtime={r} />)}
            </div>
          </section>
        )}

        <section className="space-y-3" data-testid="paper-section">
          <h2 className="text-xs lowercase text-[#666666] tracking-wider">reading</h2>
          <Link to="/paper" className="group block py-4 border-y border-[#1a201b]">
            <p className="text-white lowercase">paper</p>
            <p className="text-[#888888] lowercase text-sm mt-1">time is all you need: temporal translation and the credit assignment problem · aaai 2026.</p>
          </Link>
        </section>
      </div>
    </div>
  );
}
